import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { downloadFile } from "@/lib/download";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Upload, Loader2, FileJson } from "lucide-react";

const MODES = [
  { v: "merge", l: "Yalnızca eksik kayıtlar eklensin", d: "Mevcut kayıtlara dokunulmaz; yedekte olup sistemde olmayan kayıtlar eklenir." },
  { v: "replace", l: "Mevcut verilerin yerine geçsin", d: "Yedekteki tablolar silinip yedektekiyle değiştirilir. Yedekten sonra girilen kayıtlar kaybolur." },
];

const ResultTable = ({ rows }) => (
  <table className="w-full text-sm mt-4" data-testid="restore-result-table">
    <thead><tr className="text-left text-slate-500 text-xs"><th className="py-1">Tablo</th><th>Yedekte</th><th>Yüklenen</th></tr></thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.key} className="border-t border-slate-100" data-testid={`restore-result-${r.key}`}>
          <td className="py-1">{r.title}</td><td>{r.in_backup}</td><td className="font-semibold">{r.restored}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

export const RestoreCard = ({ onDone, resetKey }) => {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("merge");
  const [users, setUsers] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);
  useEffect(() => { setResult(null); }, [resetKey]);

  const run = async () => {
    setConfirm(false);
    setResult(null);
    try {
      setBusy("Güvenlik yedeği indiriliyor...");
      const stamp = new Date().toLocaleString("sv-SE").replace(/[-: ]/g, "").slice(0, 12);
      await downloadFile("/backup/json", `khgb_guvenlik_yedegi_${stamp}.json`, true);
    } catch (e) {
      toast.error("Güvenlik yedeği alınamadı, geri yükleme yapılmadı");
      setBusy("");
      return;
    }
    try {
      setBusy("Geri yükleniyor...");
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      fd.append("restore_users", users ? "true" : "false");
      const r = await api.post("/backup/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data.tables);
      setFile(null);
      toast.success("Geri yükleme tamamlandı");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString?.() || "Geri yükleme başarısız");
    } finally { setBusy(""); }
  };

  return (
    <Card className="p-6 border border-slate-200 shadow-sm max-w-3xl mt-6" data-testid="restore-card">
      <h3 className="font-display font-semibold text-slate-900">Yedekten Geri Yükleme</h3>
      <p className="text-sm text-slate-500 mt-1">
        Bu sistemden indirilen JSON yedeğini seçin. Başlamadan önce mevcut verilerin güvenlik yedeği hem bilgisayarınıza indirilir hem de sunucuda saklanır.
        Kullanıcı şifreleri ve yüklenen dosyalar geri yüklenmez.
      </p>
      <input ref={inputRef} type="file" accept=".json,application/json" className="hidden" data-testid="restore-file-input"
             onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); e.target.value = ""; }} />
      <div className="flex flex-wrap items-center gap-3 mt-4">
        <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={!!busy} data-testid="restore-choose-file-button">
          <FileJson className="h-4 w-4 mr-1.5" /> Yedek Dosyası Seç
        </Button>
        <span className="text-sm text-slate-600 truncate max-w-[260px]" data-testid="restore-file-name">{file ? file.name : "Dosya seçilmedi"}</span>
      </div>
      <RadioGroup value={mode} onValueChange={setMode} className="mt-5 gap-3" data-testid="restore-mode-group">
        {MODES.map((m) => (
          <label key={m.v} className={`flex gap-3 rounded-md border p-3 cursor-pointer ${mode === m.v ? "border-emerald-600 bg-emerald-50/40" : "border-slate-200"}`}>
            <RadioGroupItem value={m.v} className="mt-0.5" data-testid={`restore-mode-${m.v}`} />
            <div><div className="text-sm font-medium text-slate-900">{m.l}</div><div className="text-xs text-slate-500">{m.d}</div></div>
          </label>
        ))}
      </RadioGroup>
      <label className="flex items-start gap-2 mt-4 text-sm text-slate-700 cursor-pointer">
        <Checkbox checked={users} onCheckedChange={(v) => setUsers(!!v)} className="mt-0.5" data-testid="restore-users-checkbox" />
        <span>Kullanıcıların ad ve rol bilgilerini de geri yükle <span className="text-xs text-slate-500">(şifreler değişmez, yeni hesap açılmaz, sizin rolünüz korunur)</span></span>
      </label>
      <Button onClick={() => setConfirm(true)} disabled={!file || !!busy} className="mt-5 bg-emerald-700 hover:bg-emerald-800" data-testid="restore-start-button">
        {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />} {busy || "Geri Yüklemeyi Başlat"}
      </Button>
      {result && <ResultTable rows={result} />}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="restore-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Geri yükleme yapılsın mı?</AlertDialogTitle>
            <AlertDialogDescription>
              {mode === "replace"
                ? "Yedekteki tablolardaki mevcut kayıtların tamamı silinip yedektekiyle değiştirilecek. Yedekten sonra girilen kayıtlar kaybolacak."
                : "Yedekte olup sistemde olmayan kayıtlar eklenecek; mevcut kayıtlar değişmeyecek."}
              {" "}Önce mevcut verilerin güvenlik yedeği bilgisayarınıza indirilecek.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="restore-cancel-button">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={run} className={mode === "replace" ? "bg-rose-700 hover:bg-rose-800" : "bg-emerald-700 hover:bg-emerald-800"}
                               data-testid="restore-confirm-button">Evet, geri yükle</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
